import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Cashbox, CashboxEntry, isMultiMethodCashbox } from '@common/models/cashbox.model';
import { AnalyticsService, CashboxService, ToastService } from '@common/services';
import { UserService } from '@modules/auth/services';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import lottie from 'lottie-web';

/** Una fila del conteo: la cuenta abierta más lo que el usuario contó. */
interface CountRow {
    bankAccountId: string;
    displayName: string;
    bankAccountName: string;
    currency: string;
    countedAmount: number;
}

@Component({
    selector: 'fina-cashbox-close',
    templateUrl: './cashbox-close.component.html',
    styleUrls: ['./cashbox-close.component.scss'],
})
export class CashboxCloseComponent implements OnInit, OnDestroy {
    @Input() currentCashbox: Cashbox;
    @Input() isForcedClose: boolean = false;
    @Output() cashboxClosed = new EventEmitter<Cashbox>();
    @Output() modalClosed = new EventEmitter<void>();

    /** false solo para las cajas que quedaron abiertas con el esquema anterior. */
    isMultiMethod = true;

    rows: CountRow[] = [];
    /** Solo en el modo anterior: un único monto en dólares. */
    finalAmountUSD: number = 0;

    observaciones: string = '';
    step: 'amount' | 'observations' = 'amount';
    loading: boolean = false;
    closedCashbox: Cashbox | null = null;
    daysOpen: number = 0;

    constructor(
        private activeModal: NgbActiveModal,
        private cashboxService: CashboxService,
        private toastService: ToastService,
        private modalService: NgbModal,
        private analyticsService: AnalyticsService,
        public userService: UserService,
    ) {}

    ngOnInit(): void {
        this.currentCashbox = this.cashboxService.currentCashbox;
        this.isMultiMethod = isMultiMethodCashbox(this.currentCashbox);

        this.rows = (this.currentCashbox.entries || []).map(entry => ({
            bankAccountId: entry.bankAccountId,
            displayName: entry.displayName,
            bankAccountName: entry.bankAccountName,
            currency: entry.currency,
            countedAmount: null,
        }));

        if (this.isForcedClose) {
            const openingDate = new Date(this.currentCashbox.openingDate);
            const today = new Date();
            this.daysOpen = Math.floor((today.getTime() - openingDate.getTime()) / (1000 * 60 * 60 * 24));
        }
    }

    ngOnDestroy(): void {
        this.modalService.dismissAll();
    }

    getModalTitle(): string {
        if (this.isForcedClose) {
            return `Cierre de caja pendiente (${this.daysOpen} día${this.daysOpen > 1 ? 's' : ''})`;
        }
        return 'Cerrar caja';
    }

    getModalSubtitle(): string {
        if (this.isForcedClose) {
            return `La caja ha estado abierta por ${this.daysOpen} día${this.daysOpen > 1 ? 's' : ''}. ¿Deseas cerrar la caja?.`;
        }
        if (this.isMultiMethod) {
            return 'Cuenta cada método y anota lo contado. Las cuentas no se mezclan.';
        }
        return 'Ingrese el monto final de cierre';
    }

    /** Faltan montos por contar: el cierre exige todas las cuentas. */
    get hasMissingCount(): boolean {
        return this.rows.some(row => row.countedAmount === null || row.countedAmount === undefined || row.countedAmount < 0);
    }

    /** Entradas del cierre ya calculado, para el paso de resultados. */
    get closedEntries(): CashboxEntry[] {
        return this.closedCashbox?.entries || [];
    }

    /** Diferencias totales por moneda, omitiendo las monedas que no participaron. */
    get differenceTotals(): Array<{ currency: string; total: number }> {
        const totals = this.closedCashbox?.differenceTotals;
        if (!totals) return [];

        const currenciesInUse = new Set(this.closedEntries.map(entry => entry.currency));
        return ['VES', 'USD'].filter(currency => currenciesInUse.has(currency)).map(currency => ({ currency, total: totals[currency] || 0 }));
    }

    get hasDiscrepancy(): boolean {
        if (this.isMultiMethod) {
            return this.closedEntries.some(entry => entry.difference !== 0);
        }
        return !!this.closedCashbox && this.closedCashbox.discrepancy !== 0;
    }

    validateAmount(event: any, row: CountRow) {
        const parts = String(event.target.value).split('.');
        if (parts[0] && parts[0].length >= 11) {
            event.target.value = parts[0].substring(0, 10) + (parts[1] ? '.' + parts[1] : '');
            row.countedAmount = parseFloat(event.target.value);
        }
        if (parts[1] && parts[1].length > 2) {
            event.target.value = parts[0] + '.' + parts[1].substring(0, 2);
            row.countedAmount = parseFloat(event.target.value);
        }
    }

    cancelClose() {
        if (this.isForcedClose) {
            this.toastService.alert('Atención', 'Se le volverá a solicitar el cierre de caja en su próximo inicio de sesión.');
            setTimeout(() => {
                this.toastService.toasts = [];
            }, 5000);
        }
        this.activeModal.dismiss('cancel');
    }

    /**
     * Cierra la caja. Hasta este momento no se conoce el esperado: el api lo calcula al
     * cerrar y recién entonces se muestran las diferencias.
     */
    proceedToObservations() {
        if (this.isMultiMethod && this.hasMissingCount) {
            this.toastService.error('Error', 'Ingresa el monto contado de cada método de pago.');
            return;
        }

        if (!this.isMultiMethod && (this.finalAmountUSD === undefined || this.finalAmountUSD === null)) {
            this.toastService.error('Error', 'Por favor ingrese el monto final.');
            return;
        }

        this.loading = true;

        const close$ = this.isMultiMethod
            ? this.cashboxService.closeCashBox(this.rows.map(row => ({ bankAccountId: row.bankAccountId, countedAmount: Number(row.countedAmount) })))
            : this.cashboxService.closeLegacyCashBox(this.finalAmountUSD);

        close$.subscribe({
            next: closedCashbox => {
                this.closedCashbox = closedCashbox;
                this.analyticsService.analyticsCapture('cashbox_closed', {
                    methods_count: closedCashbox.entries?.length || 0,
                    has_discrepancy: this.hasDiscrepancy,
                });
                this.step = 'observations';
                this.loading = false;

                if (!this.hasDiscrepancy) {
                    setTimeout(() => this.initializeAnimation(), 100);
                }
            },
            error: error => {
                console.error('Error closing cashbox:', error);
                this.toastService.error(error.error?.title || 'Error', error.error?.message || 'No se pudo cerrar la caja. Por favor intente nuevamente.');
                this.loading = false;
            },
        });
    }

    closeCashbox() {
        if (!this.closedCashbox) {
            console.error('No closed cashbox data available');
            return;
        }

        this.loading = true;
        this.cashboxService.addObservationsToCashbox(this.closedCashbox._id, this.observaciones).subscribe({
            next: updatedCashbox => {
                this.toastService.success('Cierre de caja completado', 'El cierre de caja se ha completado exitosamente.');
                this.cashboxClosed.emit(updatedCashbox);
                this.modalService.dismissAll();
                this.modalClosed.emit();
            },
            error: error => {
                console.error('Error adding observations:', error);
                this.toastService.error('Error', 'No se pudieron agregar las observaciones al cierre de caja.');
            },
            complete: () => {
                this.loading = false;
            },
        });
    }

    initializeAnimation() {
        const animationContainer = document.getElementById('animation-confetti');
        if (!animationContainer) return;

        animationContainer.innerHTML = '';
        try {
            lottie.loadAnimation({
                container: animationContainer,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                path: 'assets/animations/confetti.json',
            });
        } catch (error) {
            console.error('Error initializing animation:', error);
        }
    }

    @HostListener('window:popstate', ['$event'])
    onPopState(event: any) {
        this.modalService.dismissAll();
    }
}
